import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AccountService } from '@shared/services/account.service';
import { NotificationService } from '@shared/services/notification.service';
import { IAccount, UserService } from '@shared/services/user.service';

@Component({
	selector: 'app-ipsettings',
	templateUrl: './ipsettings.component.html',
	styleUrl: './ipsettings.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class IpsettingsComponent {
	public isLoading: boolean = false;
	public accountName: string = '';
	public inputsReadOnly: boolean = false;
	public allowedIpsString: string = '';
	public allowedIpsValid: boolean = true;
	public invalidIps: string[] = [];
	public attemptedSave: boolean = false;

	private originalAccountName: string = '';
	private originalAllowedIpsString: string = '';
	private currentAccount: IAccount | null = null;
	/** Carried from the account read in `loadData` so `save()` can echo it back untouched. The account PUT is a full replace - not sending it clears the account's default engine. */
	private defaultEngine: string | null = null;

	constructor(
		private userService: UserService,
		private accountService: AccountService,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		const account = this.userService.account!;
		this.currentAccount = account;
		this.isLoading = true;
		this.accountName = '';
		this.allowedIpsString = '';
		this.loadData(account);
	}

	loadData(account: IAccount): void {
		this.isLoading = true;
		if (account) {
			this.accountService.getAccount().subscribe({
				next: (acct) => {
					this.accountName = acct.result.name || '';
					this.allowedIpsString = (acct.result.allowedIPs || []).join(', ');
					this.defaultEngine = acct.result.defaultEngine ?? this.currentAccount?.defaultEngine ?? null;
					this.originalAccountName = this.accountName;
					this.originalAllowedIpsString = this.normalizeIpsString(this.allowedIpsString);
					this.inputsReadOnly = false;
					setTimeout(() => (this.isLoading = false), 120);
				},
				error: () => {
					this.notificationService.notify(['administration.notifications.accountReadFailedHeader'], ['administration.notifications.accountReadFailed', { name: this.currentAccount?.name || '' }], 'error');
					this.inputsReadOnly = true;
					setTimeout(() => (this.isLoading = false), 120);
				},
			});
		}
	}

	save(): void {
		this.attemptedSave = true;
		this.validateAllowedIps();
		if (!this.allowedIpsValid) {
			return;
		}
		this.isLoading = true;
		const allowedIps = (this.allowedIpsString || '')
			.split(',')
			.map((s) => s.trim())
			.filter((s) => s.length > 0);

		const name = this.accountName || '';

		this.accountService.updateAccount(name, allowedIps, this.defaultEngine).subscribe({
			next: () => {
				this.attemptedSave = false;
				// update originals so buttons are disabled after save
				this.originalAccountName = this.accountName;
				this.originalAllowedIpsString = this.normalizeIpsString(this.allowedIpsString);
				this.notificationService.notify(['administration.notifications.accountUpdateSuccessHeader'], ['administration.notifications.accountUpdateSuccess', { name: this.accountName }], 'success');
				this.isLoading = false;
			},
			error: () => {
				this.isLoading = false;
				this.notificationService.notify(['administration.notifications.accountUpdateFailedHeader'], ['administration.notifications.accountUpdateFailed', { name: this.currentAccount?.name || '' }], 'error');
			},
		});
	}

	cancel(): void {
		this.accountName = this.originalAccountName;
		this.allowedIpsString = this.originalAllowedIpsString;
		// Clear any validation/error state when cancelling
		this.attemptedSave = false;
		this.invalidIps = [];
		this.allowedIpsValid = true;
	}

	public hasChanges(): boolean {
		const normCurrent = this.normalizeIpsString(this.allowedIpsString);
		return (this.accountName || '') !== (this.originalAccountName || '') || normCurrent !== (this.originalAllowedIpsString || '');
	}

	private normalizeIpsString(input: string): string {
		return (input || '')
			.split(',')
			.map((s) => s.trim())
			.filter((s) => s.length > 0)
			.join(', ');
	}

	private isValidIp(addr: string): boolean {
		if (!addr) {
			return false;
		}

		const ipv4 = /^(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;

		const ipv6 =
			/^(?:(?:[0-9A-F]{1,4}:){7}[0-9A-F]{1,4}|(?:[0-9A-F]{1,4}:){1,7}:|:(?::[0-9A-F]{1,4}){1,7}|(?:[0-9A-F]{1,4}:){1,6}:[0-9A-F]{1,4}|(?:[0-9A-F]{1,4}:){1,5}(?::[0-9A-F]{1,4}){1,2}|(?:[0-9A-F]{1,4}:){1,4}(?::[0-9A-F]{1,4}){1,3}|(?:[0-9A-F]{1,4}:){1,3}(?::[0-9A-F]{1,4}){1,4}|(?:[0-9A-F]{1,4}:){1,2}(?::[0-9A-F]{1,4}){1,5})$/i;
		if (ipv4.test(addr)) return true;
		if (ipv6.test(addr)) return true;
		return false;
	}

	public validateAllowedIps(): void {
		this.invalidIps = [];
		const rawEntries = (this.allowedIpsString || '').split(',').map((s) => s.trim());
		// If the whole input is empty (single blank entry or undefined), that's valid
		if (rawEntries.length === 1 && (!rawEntries[0] || rawEntries[0].length === 0)) {
			this.allowedIpsValid = true;
			return;
		}
		// If there are any blank entries among multiple entries, that's invalid
		const emptyCount = rawEntries.filter((s) => s.length === 0).length;
		const nonEmptyEntries = rawEntries.filter((s) => s.length > 0);
		for (const e of nonEmptyEntries) {
			if (!this.isValidIp(e)) {
				this.invalidIps.push(e);
			}
		}
		// If there were blank entries among multiple comma-separated values,
		// consider the overall input invalid but do not add '<blank>' to `invalidIps`.
		this.allowedIpsValid = this.invalidIps.length === 0 && emptyCount === 0;
	}
}
